import { useMemo, useCallback, useState } from 'react';
import {
  CombinatorRendererProps,
  createCombinatorRenderInfos,
  createDefaultValue,
  JsonSchema,
  isOneOfControl,
  rankWith,
  ControlElement,
} from '@jsonforms/core';
import { JsonFormsDispatch, withJsonFormsOneOfProps } from '@jsonforms/react';
import { FormControl, InputLabel, Select, MenuItem, Box, Paper, Typography } from '@mui/material';
import { OPTIONAL_FIELDS_ADDER_TYPE } from '../OptionalFieldsAdder/OptionalFieldsAdder';

/** Build UI schema: required fields as Controls + OptionalFieldsAdder at the end */
function buildRequiredPlusOptionalUISchema(schema: JsonSchema): { type: string; elements: unknown[] } {
  const props = schema?.properties as Record<string, JsonSchema> | undefined;
  const required = (schema?.required as string[] | undefined) ?? [];
  const elements: unknown[] = [];

  required.forEach((key) => {
    const propSchema = props?.[key];
    if (propSchema) {
      elements.push({
        type: 'Control',
        scope: `#/properties/${key}`,
        label: (propSchema.title as string) ?? key,
      } as ControlElement);
    }
  });

  elements.push({ type: OPTIONAL_FIELDS_ADDER_TYPE });
  return { type: 'VerticalLayout', elements };
}

/** Renders the selected oneOf branch with required-only + optional-adder UI. Exported for use in EnableableOneOfRenderer. */
export function StrategyConfigForm({
  schema,
  path,
  renderers,
  cells,
}: {
  schema: JsonSchema;
  path: string;
  renderers: CombinatorRendererProps['renderers'];
  cells: CombinatorRendererProps['cells'];
}) {
  const uischema = useMemo(() => buildRequiredPlusOptionalUISchema(schema), [schema]);
  return (
    <JsonFormsDispatch
      schema={schema}
      uischema={uischema}
      path={path}
      renderers={renderers}
      cells={cells}
    />
  );
}

export const OneOfDropdownRenderer = ({
  handleChange,
  schema,
  path,
  renderers,
  cells,
  rootSchema,
  id,
  visible,
  indexOfFittingSchema,
  uischema,
  uischemas,
}: CombinatorRendererProps) => {
  const stripOptionalProperties = useCallback(
    (data: unknown, branchSchema: JsonSchema): unknown => {
      if (data === null || data === undefined || typeof data !== 'object') {
        return data;
      }
      const obj = data as Record<string, unknown>;
      const props = (branchSchema.properties as Record<string, JsonSchema>) ?? {};
      const required = new Set((branchSchema.required as string[] | undefined) ?? []);
      const result: Record<string, unknown> = {};

      Object.keys(props).forEach((key) => {
        if (required.has(key)) {
          result[key] = obj[key];
        }
      });

      return result;
    },
    []
  );

  const initialIndex =
    indexOfFittingSchema !== undefined && indexOfFittingSchema >= 0 ? indexOfFittingSchema : 0;
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  const oneOfRenderInfos = useMemo(
    () =>
      createCombinatorRenderInfos(
        (schema as JsonSchema).oneOf ?? [],
        rootSchema,
        'oneOf',
        uischema as ControlElement,
        path,
        uischemas
      ),
    [schema, rootSchema, uischema, path, uischemas]
  );

  const handleDropdownChange = useCallback(
    (event: { target: { value: string } }) => {
      const newIndex = Number(event.target.value);
      if (newIndex === selectedIndex) return;
      const newSchema = oneOfRenderInfos[newIndex]?.schema;
      if (newSchema) {
        const defaults = createDefaultValue(newSchema, rootSchema);
        const requiredOnly = stripOptionalProperties(defaults, newSchema as JsonSchema);
        handleChange(path, requiredOnly);
        setSelectedIndex(newIndex);
      }
    },
    [handleChange, path, rootSchema, oneOfRenderInfos, selectedIndex, stripOptionalProperties]
  );

  if (!visible) return null;
  if (!oneOfRenderInfos.length) return null;

  const selectedInfo = oneOfRenderInfos[selectedIndex];
  if (!selectedInfo) return null;

  const controlElement = uischema as ControlElement;
  const rawLabel = controlElement?.label;
  const typeLabel =
    typeof rawLabel === 'string'
      ? `${rawLabel} type`
      : typeof rawLabel === 'object' && rawLabel !== null && 'text' in rawLabel
        ? `${(rawLabel as { text?: string }).text ?? 'Type'} type`
        : 'Type';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <FormControl size="small" fullWidth>
        <InputLabel id={`${id}-oneOf-label`}>{typeLabel}</InputLabel>
        <Select
          labelId={`${id}-oneOf-label`}
          value={String(selectedIndex)}
          label={typeLabel}
          onChange={handleDropdownChange}
        >
          {oneOfRenderInfos.map((info, idx) => (
            <MenuItem key={info.label} value={String(idx)}>
              {info.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mt: 0.5,
          width: '100%',
          boxSizing: 'border-box',
          bgcolor: 'action.hover',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          {selectedInfo.label} config
        </Typography>
        <StrategyConfigForm
          schema={selectedInfo.schema}
          path={path}
          renderers={renderers}
          cells={cells}
        />
      </Paper>
    </Box>
  );
};

export const oneOfDropdownTester = rankWith(4, isOneOfControl);

export default withJsonFormsOneOfProps(OneOfDropdownRenderer);
